import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { randomUUID } from '@/shared/lib/uuid';

export type ApartmentStatus =
  | 'available'
  | 'reserved'
  | 'contracted'
  | 'sold'
  | 'delivered'
  | 'cancelled';

export type ApartmentLayout =
  | 'studio'
  | 'one_room'
  | 'two_room'
  | 'three_room'
  | 'four_room_plus'
  | 'penthouse'
  | 'commercial';

export type PaymentMethod = 'cash' | 'bank_transfer' | 'card';

export type ActiveReservation = {
  id: string;
  customerId: string;
  reservationFee: string | null; // tiyin string (BigInt-safe)
  paymentMethod: PaymentMethod | null;
  paymentRef: string | null;
  expiresAt: string;
  reservedAt: string;
  customer: { id: string; firstName: string; lastName: string; phone: string };
  createdByUser: { id: string; fullName: string };
};

export type Apartment = {
  id: string;
  projectId: string;
  blockId: string;
  floorId: string;
  number: string;
  fullCode: string;
  layoutType: ApartmentLayout;
  areaSqm: string;
  rooms: number;
  bathrooms: number;
  balconies: number;
  basePrice: string;
  pricePerSqm: string;
  status: ApartmentStatus;
  currentReservationId: string | null;
  currentContractId: string | null;
  features: Record<string, unknown>;
  block?: { id: string; code: string; name: string };
  floor?: { id: string; number: number; name: string | null };
  project?: { id: string; code: string; name: string };
  currentReservation?: ActiveReservation | null;
};

export type GridCell = {
  id: string;
  number: string;
  fullCode: string;
  status: ApartmentStatus;
  layoutType: ApartmentLayout;
  areaSqm: string;
  basePrice: string;
};
export type GridFloor = { id: string; number: number; name: string | null; units: GridCell[] };
export type GridBlock = {
  id: string;
  code: string;
  name: string;
  position: number;
  floors: GridFloor[];
};
export type Grid = {
  blocks: GridBlock[];
  totalApartments: number;
  counts: Partial<Record<ApartmentStatus, number>>;
};

export function useApartmentGrid(projectId: string | undefined) {
  return useQuery({
    queryKey: ['apartments-grid', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ data: Grid }>('/apartments/grid', { params: { projectId } });
      return res.data.data;
    },
  });
}

export function useApartment(id: string | undefined) {
  return useQuery({
    queryKey: ['apartment', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: Apartment }>(`/apartments/${id}`);
      return res.data.data;
    },
  });
}

export type BulkUnitTemplateInput = {
  unitIndex: number;
  number?: string;
  layoutType: ApartmentLayout;
  areaSqm: number;
  rooms: number;
  bathrooms?: number;
  balconies?: number;
};

export type FloorGroupInput = {
  floors: { from: number; to: number };
  unitsPerFloor: number;
  /** Tiyin per m² as a decimal string (BigInt-safe over the wire). */
  pricePerSqm: string;
  unitTemplates: BulkUnitTemplateInput[];
};

export type BulkCreateInput = {
  blockId: string;
  numberingPattern?: 'per_floor_reset' | 'continuous';
  groups: FloorGroupInput[];
};

export function useBulkCreateApartments(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkCreateInput) => {
      const idempotencyKey = randomUUID();
      const res = await api.post<{ data: { floorsCreated: number; apartmentsCreated: number } }>(
        '/apartments/bulk',
        input,
        { headers: { 'Idempotency-Key': idempotencyKey } },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['apartments-grid', projectId] });
      void qc.invalidateQueries({ queryKey: ['blocks', projectId] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export type TransitionInput =
  | {
      action: 'reserve';
      customerId: string;
      hours?: number;
      reservationFee?: string;
      paymentMethod?: PaymentMethod;
      paymentRef?: string;
    }
  | { action: 'release'; reason?: string };

export function useTransitionApartment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: TransitionInput }) => {
      const res = await api.post<{ data: Apartment }>(
        `/apartments/${args.id}/transition`,
        args.input,
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['apartments-grid', projectId] });
      void qc.invalidateQueries({ queryKey: ['apartment', data.id] });
    },
  });
}

export type UpdateApartmentInput = Partial<{
  number: string;
  layoutType: ApartmentLayout;
  areaSqm: number;
  rooms: number;
  bathrooms: number;
  balconies: number;
  /** Tiyin as decimal string. */
  basePrice: string;
  features: Record<string, unknown>;
}>;

export function useUpdateApartment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: UpdateApartmentInput }) => {
      const res = await api.patch<{ data: Apartment }>(`/apartments/${args.id}`, args.input);
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['apartments-grid', projectId] });
      void qc.invalidateQueries({ queryKey: ['apartment', data.id] });
    },
  });
}

export function useDeleteApartment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/apartments/${id}`);
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['apartments-grid', projectId] });
    },
  });
}
